import { useIntl } from 'react-intl'
import { CrossCameraPipelineWrite } from '@/api/crossCameraApi'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useCreateCrossCameraPipeline,
  useUpdateCrossCameraPipeline
} from '@/queries/crossCameraQueries'
import { showToast } from '@/theme/notifications'
import {
  CrossCameraApp,
  CrossCameraPipeline,
  CrossCameraTrackingModel
} from '@/types/crossCamera'
import { CrossCameraPipelineForm } from './CrossCameraPipelineForm'

type CrossCameraPipelineFormHandlerProps = {
  pipeline?: CrossCameraPipeline
  applications: CrossCameraApp[]
  usedApplicationIds: string[]
  models: CrossCameraTrackingModel[]
  onClose: () => void
}

export const CrossCameraPipelineFormHandler = ({
  pipeline,
  applications,
  usedApplicationIds,
  models,
  onClose
}: CrossCameraPipelineFormHandlerProps) => {
  const intl = useIntl()
  const { mutateAsync: createPipeline, isPending: isCreating } =
    useCreateCrossCameraPipeline()
  const { mutateAsync: updatePipeline, isPending: isUpdating } =
    useUpdateCrossCameraPipeline()

  const handleSubmit = async (data: CrossCameraPipelineWrite) => {
    try {
      if (pipeline) {
        await updatePipeline({ pipelineId: pipeline.id, ...data })
      } else {
        await createPipeline(data)
      }
      showToast(
        intl.formatMessage({ id: 'crossCamera.pipelines.create.success' }),
        'success'
      )
      onClose()
    } catch (error) {
      showToast(
        extractErrorMessage(
          error,
          intl.formatMessage({ id: 'crossCamera.pipelines.create.error' })
        ),
        'error'
      )
    }
  }

  return (
    <CrossCameraPipelineForm
      pipeline={pipeline}
      applications={applications}
      usedApplicationIds={usedApplicationIds}
      models={models}
      isSubmitting={isCreating || isUpdating}
      onSubmit={(values) => void handleSubmit(values)}
      onCancel={onClose}
    />
  )
}
